import React from 'react';
import { useMachine } from '@xstate/react';
import { dropdownInputMachine } from '@trqk/utils';
import { useDeepCompareMemo } from 'use-deep-compare';

import { BaseDropdownInput } from '.';
import { sortOptionsWithDefaultFirst } from './helpers';

/**
 * The default configuration of a DropdownInput component. Has its own private instance of a
 * dropdownInputMachine and does not communicate with other state machines. See {@link BaseDropdownInput}
 * for more details on the base dropdown input component itself.
 *
 * @parma props - see {@link DropdownInputProps}
 */
export const DropdownInput = (props: DropdownInputProps): JSX.Element => {
    const {
        addClasses,
        defaultId,
        disabled,
        disabledOptionIds = new Set(),
        elementId,
        handleChange,
        height,
        highlightIfSelected,
        highlightWhenOpen,
        maxListDisplayCount,
        resetTimestamp,
        sortOptionsList = true,
        StatusIcon,
        width,
    } = props;
    const options = useDeepCompareMemo((): DropdownInputOption[] | null => {
        if (props.options && sortOptionsList) {
            return sortOptionsWithDefaultFirst(props.options, defaultId);
        }

        return props.options ?? null;
    }, [sortOptionsList, props.options, sortOptionsWithDefaultFirst]);

    const optionLists = useDeepCompareMemo(():
        | DropdownInputOption[][]
        | null => {
        if (props.optionLists && sortOptionsList) {
            return props.optionLists.map(
                (optionList: DropdownInputOption[]): DropdownInputOption[] =>
                    sortOptionsWithDefaultFirst(optionList, defaultId),
            );
        }

        return props.optionLists ?? null;
    }, [sortOptionsList, props.optionLists, sortOptionsWithDefaultFirst]);

    const flatOptionLists = optionLists === null ? [] : optionLists.flat(1);
    const filteredOptions = options ?? flatOptionLists;

    const [current, send] = useMachine(dropdownInputMachine, {
        context: {
            disabledOptionIds,
            options,
            optionLists,
            filteredOptions,
            selectedId: defaultId,
        },
    });

    return (
        <BaseDropdownInput
            current={current}
            send={send}
            defaultId={defaultId}
            sortOptionsList={sortOptionsList}
            addClasses={addClasses}
            disabled={disabled}
            elementId={elementId}
            handleChange={handleChange}
            height={height}
            options={options}
            optionLists={optionLists}
            highlightIfSelected={highlightIfSelected}
            highlightWhenOpen={highlightWhenOpen}
            maxListDisplayCount={maxListDisplayCount}
            resetTimestamp={resetTimestamp}
            StatusIcon={StatusIcon}
            width={width}
        />
    );
};
